// Mirrored by the Cloudflare response header rule "X-Robots-Tag noindex for
// private link parameters" in vicampo.de/.at/.ch — this list plus `hash`.
// Keep both in sync (VIP-12469).
const PRIVATE_PARAMS = ['ph', 'email', 'coupon', 'doi', 'doi_voucher']
const LINK_TAGS = new Set(['canonical', 'alternate'])

export const NO_INDEX_FOLLOW = 'noindex,follow'
export const NO_INDEX_NOFOLLOW = 'noindex,nofollow'
export const INDEX_FOLLOW = 'index,follow'

export const createMetaObject = async ({ title, ...rest }) => {
  const { SiteConfig } = await _getModules()

  const prefix = SiteConfig.devMode ? '[ 👾] ' : ''
  const titleTemplate = `${prefix}%s | ${SiteConfig.name}`
  const pagePrivate = containsPrivateInfo()

  const { meta, link } = mapMetaData(rest, pagePrivate)

  return { titleTemplate, title, meta, link }
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [{ default: SiteConfig }, { default: router }] = await Promise.all([
      import('@/services/SiteConfig'),
      import('@/router')
    ])
    cache = {
      SiteConfig,
      router
    }
    return cache
  }
})()

export const containsPrivateInfo = () => {
  // The server must not read the query: Cloudflare's PDP cache key excludes
  // the private parameters (VIP-12469), so a noindex derived from them would
  // be cached under the clean URL — and served to Google. Server-side noindex
  // for private URLs comes from the edge X-Robots-Tag rule instead; the
  // browser still sets the meta after hydration.
  if (import.meta.env.SSR) return false
  const query = new URLSearchParams(location.search)
  return PRIVATE_PARAMS.some((p) => query.has(p))
}

const mapMetaData = (data, noIndex) => {
  // [ { rel: 'something', random_prop: '123' } ]
  const link = Object.keys(data)
    .filter((key) => LINK_TAGS.has(key))
    .reduce(
      (tags, key) => [
        ...tags,
        ...data[key].map((content) => ({ rel: key, ...content }))
      ],
      []
    )

  // [ { vmid: '', name: '', content: '' } ]
  const meta = Object.keys(data)
    .filter((key) => !LINK_TAGS.has(key))
    .reduce((tags, key) => {
      if (!data[key]) {
        return tags
      }
      if (key === 'robots' && noIndex) {
        // Private params only ever tighten indexing, so an explicit nofollow
        // must survive this branch instead of being weakened to follow.
        const content =
          data[key] === NO_INDEX_NOFOLLOW ? NO_INDEX_NOFOLLOW : NO_INDEX_FOLLOW
        return [...tags, { vmid: key, name: key, content }]
      }
      return [...tags, { vmid: key, name: key, content: data[key] }]
    }, [])

  return { meta, link }
}
